import { AnalyticReportAccessDTO, User } from '@jooxter/api';
import { IUserPermissions } from './types';
import { NO_PERMISSIONS } from './constants';
import { isUserWithCapabilities } from './user.type-guard';

export class PermissionManager {
  static #isInternalConstructing = false;
  static #instance: PermissionManager;
  #user?: User;
  #userAnalytics?: AnalyticReportAccessDTO[];
  #permissions: IUserPermissions = NO_PERMISSIONS;

  private constructor(user?: User, userAnalytics?: AnalyticReportAccessDTO[]) {
    // singleton
    if (!PermissionManager.#isInternalConstructing) {
      throw new TypeError('PrivateConstructor is not constructable');
    }

    this.#initialize(user, userAnalytics);
    PermissionManager.#isInternalConstructing = false;
  }

  #initialize(user?: User, userAnalytics?: AnalyticReportAccessDTO[]): void {
    if (isUserWithCapabilities(user)) {
      this.#user = user;
      this.#userAnalytics = userAnalytics;
      this.#permissions = this.#getPermissions(user, userAnalytics);
      return;
    }

    // No user yet is the normal loading state (usePermissions runs before useFetchUser resolves):
    // don't warn, it was the single largest source of Sentry events (ND-5676).
    if (user) {
      console.warn('PermissionManager: user has no capabilities, permissions will not be available');
    }
    this.#permissions = NO_PERMISSIONS;
  }

  static getInstance(user?: User, userAnalytics?: AnalyticReportAccessDTO[]): PermissionManager {
    PermissionManager.#isInternalConstructing = true;
    if (!this.#instance) {
      return (this.#instance = new PermissionManager(user, userAnalytics));
    }

    const updateInstance = this.#instance.#user !== user || this.#instance.#userAnalytics !== userAnalytics;

    if (updateInstance) {
      return (this.#instance = new PermissionManager(user, userAnalytics));
    }

    PermissionManager.#isInternalConstructing = false;
    return this.#instance;
  }

  setUser(user?: User): void {
    this.#initialize(user);
  }

  getPermissions(): IUserPermissions {
    return this.#permissions;
  }

  #getPermissions(user: User, userAnalytics?: AnalyticReportAccessDTO[]): IUserPermissions {
    const hasBooking = !!user.capabilities?.features?.booking;
    const hasCollaborators = !!user.capabilities?.features?.collaborators;
    const hasMeetup = !!user.capabilities?.features?.meetup;
    const allowBookingOverlap = !!user.capabilities?.features?.allowBookingOverlap;

    return {
      booking: hasBooking,
      allowBookingOverlap,
      scheduler: hasBooking,
      list: hasBooking,
      floorPlan: hasBooking,
      map: hasBooking,
      collaborators: hasCollaborators,
      analytics: new Map(
        userAnalytics
          ?.filter(
            (r): r is Required<Pick<AnalyticReportAccessDTO, 'report' | 'enabled'>> =>
              r.report !== undefined && r.report !== null && r.enabled !== undefined && r.enabled !== null
          )
          .map((report) => [report.report, report.enabled])
      ),
      changePassword: !!user.capabilities?.features?.changePassword,
      week: !!user.capabilities?.features?.week,
      meetup: hasMeetup && hasBooking,
      manageSpaces: !!(user.capabilities?.features?.resourceManager || user.capabilities?.features?.locationManager),
    };
  }
}
